import * as React from 'react';
import { isIncreasedFlexibilityEnabled } from '@helpcenter/feature-flags';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';
import { ElementRoot } from 'view/common/element-root';
import { ForgeFooterModule } from 'view/forge-ui/portal-footer';
import { ForgeHeaderModule } from 'view/forge-ui/portal-header';
import { useIsPageInEditMode } from 'view/layout-next/controllers';
import { Box, xcss } from '@atlaskit/primitives';
import { trackError } from '@atlassian/help-center-common-util/analytics';
import type {
    forgeElementFragment$key,
    HelpLayoutForgeElementPages,
} from './__generated__/forgeElementFragment.graphql';

export interface ForgeElementViewerProps {
    forgeElementRef: forgeElementFragment$key;
}
export const ForgeElement: React.FC<ForgeElementViewerProps> = ({ forgeElementRef }) => {
    di(useIsPageInEditMode, ForgeFooterModule, ForgeHeaderModule);

    const [isPageInEditMode] = useIsPageInEditMode();
    const forgeElementData = useFragment(
        graphql`
            fragment forgeElementFragment on HelpLayoutForgeElement {
                forgeElementType
                forgeElementPage
            }
        `,
        forgeElementRef
    );
    const { forgeElementType, forgeElementPage } = forgeElementData;
    const isValidForgeElement = forgeElementType && ['HEADER_AND_SUBHEADER', 'FOOTER'].includes(forgeElementType);

    const propsForForgeModules = React.useMemo(
        () => ({
            // eslint-disable-next-line relay/no-future-added-value
            page: forgeElementPage as Exclude<HelpLayoutForgeElementPages, '%future added value'>,
            noWrapper: true, // this is to ignore extra spacings done from within the Forge Components
            allowCustomPadding: true,
        }),
        [forgeElementPage]
    );

    React.useEffect(() => {
        if (!isValidForgeElement) {
            trackError('forge.element.type.invalid', { forgeElementType }, 'Invalid Forge Element Type received');
        }
    }, [isValidForgeElement, forgeElementType]);

    const getForgeElement = React.useCallback(() => {
        if (forgeElementType === 'HEADER_AND_SUBHEADER') {
            return <ForgeHeaderModule {...propsForForgeModules} />;
        }
        if (forgeElementType === 'FOOTER') {
            return <ForgeFooterModule {...propsForForgeModules} />;
        }
        return null;
    }, [forgeElementType, propsForForgeModules]);

    return isValidForgeElement ? (
        <ElementRoot removePadding removeMargin={isIncreasedFlexibilityEnabled()}>
            <Box xcss={isPageInEditMode ? [forgeElementEditStyle] : undefined}>{getForgeElement()}</Box>
        </ElementRoot>
    ) : null;
};

const forgeElementEditStyle = xcss({
    pointerEvents: 'none',
});
